import { sendAppEvent } from "../../Utils";

type SubmitReplyParams<TComment> = {
  newReply: string;
  isSubmittingReply: boolean;
  replySubmitInFlightRef: { current: boolean };
  isMobile: boolean;
  setActiveAnnotationIds: (ids: string[]) => void;
  setToast: (message: string) => void;
  documentId?: string | null;
  commentLocation: string;
  submitComment: (
    documentId: string,
    content: string,
    parent?: TComment,
  ) => Promise<{ success: boolean; message: string }>;
  comment: TComment;
  setNewReply: (value: string) => void;
  setIsSubmittingReply: (value: boolean) => void;
  onSubmitStarted?: () => void;
  onSubmitFailed?: (reply: string, message: string) => void;
  onSubmitSucceeded?: () => void;
};

export async function submitReply<TComment>({
  newReply,
  isSubmittingReply,
  replySubmitInFlightRef,
  isMobile,
  setActiveAnnotationIds,
  setToast,
  documentId,
  commentLocation,
  submitComment,
  comment,
  setNewReply,
  setIsSubmittingReply,
  onSubmitStarted,
  onSubmitFailed,
  onSubmitSucceeded,
}: SubmitReplyParams<TComment>): Promise<void> {
  if (replySubmitInFlightRef.current || isSubmittingReply) {
    return;
  }
  if (newReply.trim() === "") {
    return;
  }
  if (isMobile) {
    setActiveAnnotationIds([]);
    setToast("Reply submitted");
  }

  const reply = newReply;
  setNewReply("");
  onSubmitStarted?.();
  replySubmitInFlightRef.current = true;
  setIsSubmittingReply(true);

  sendAppEvent("CleanCopy - Submit Comment", {
    documentId,
    location: commentLocation,
    isReply: true,
  });

  try {
    const { success, message } = await submitComment(documentId as string, reply, comment);

    if (!success) {
      if (onSubmitFailed) {
        onSubmitFailed(reply, message);
        return;
      }
      setToast(message);
      setNewReply(reply);
      return;
    }

    onSubmitSucceeded?.();
  } finally {
    replySubmitInFlightRef.current = false;
    setIsSubmittingReply(false);
  }
}

type CancelReplyParams = {
  isSubmittingReply: boolean;
  setNewReply: (value: string) => void;
  onCancel?: () => void;
};

export function cancelReply({
  isSubmittingReply,
  setNewReply,
  onCancel,
}: CancelReplyParams): void {
  if (isSubmittingReply) {
    return;
  }
  setNewReply("");
  onCancel?.();
}
